import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCheck,
  CircleX,
  FileUp,
  Printer,
  Reply,
  Save,
  Trash2,
  TriangleAlert,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { controlMachine } from '@/config/controlStates';
import { useControlContext } from '@/contexts/ControlContext';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import useControlActions from '@/hooks/useControlActions';
import { DETAIL_MODE, DETAIL_TYPE, useDetailMode } from '@/hooks/useDetailMode';
import { getUserInfo } from '@/lib/auth';

const CONTROL_STEPS = ['information', 'details', 'risk', 'approvals'];

export const ControlActionsHead = () => {
  const navigate = useNavigate();

  const actions = useControlActions();
  const { mode } = useDetailMode(DETAIL_TYPE.CONTROL);
  const { form } = useControlContext();

  const { showConfirmModal } = useConfirmModal();

  const handleDraftClick = () => {
    showConfirmModal({
      title: 'Save Draft',
      description:
        'Are you sure you want to save your changes as a draft and go back to the list?',
      onConfirm:
        mode === DETAIL_MODE.EDIT ? actions.saveDraft : actions.createDraft,
    });
  };

  const handleDeleteClick = () => {
    showConfirmModal({
      title: 'Delete Draft',
      description:
        'This action permanently deletes this draft and cannot be undone. Are you sure?',
      onConfirm: actions.deleteDraft,
      variant: 'destructive',
    });
  };

  return (
    <>
      <Button
        onClick={() =>
          navigate('/controls', {
            state: mode === DETAIL_MODE.EDIT ? 'pending' : 'master',
          })
        }
        variant="outline"
      >
        <Reply className="h-4 w-4 mr-2" />
        {mode === DETAIL_MODE.EDIT
          ? 'Back to Pending List'
          : 'Back to Master List'}
      </Button>
      {(mode === DETAIL_MODE.NEW ||
        (mode === DETAIL_MODE.EDIT && form.status === 'DRAFT')) && (
        <Button onClick={handleDraftClick} variant="outline">
          <Save className="h-4 w-4 mr-2" />
          Save Draft
        </Button>
      )}
      {mode === DETAIL_MODE.EDIT && form.status === 'DRAFT' && (
        <Button
          className="border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
          onClick={handleDeleteClick}
          variant="outline"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete Draft
        </Button>
      )}
    </>
  );
};

export const ControlActionsTail = ({
  canApprove = true,
  reviews = [],
  allowedActions: allowedActionsProp,
  blockingCommentsCount = 0,
}) => {
  const actions = useControlActions();
  const { mode } = useDetailMode(DETAIL_TYPE.CONTROL);
  const { activeTab, form, setActiveTab } = useControlContext();
  const { riskGrading, riskResidual } = form;

  const { showConfirmModal } = useConfirmModal();

  const isFirstTab = activeTab === CONTROL_STEPS[0];
  const isLastTab = activeTab === CONTROL_STEPS[CONTROL_STEPS.length - 1];
  const allowedActions = useMemo(() => {
    if (allowedActionsProp) return allowedActionsProp;

    const userInfo = getUserInfo();
    const reviewerPipeline = form.reviewerPipeline || [];

    return form.status
      ? controlMachine.getActions(
          form.status,
          userInfo?.sub,
          reviewerPipeline,
          form.currentActorIndex ?? 0,
          form.requestorId,
          userInfo?.['cognito:groups']
        )
      : [];
  }, [
    allowedActionsProp,
    form.currentActorIndex,
    form.requestorId,
    form.reviewerPipeline,
    form.status,
  ]);

  const handlePreviousClick = () => {
    const current = CONTROL_STEPS.indexOf(activeTab);

    setActiveTab(CONTROL_STEPS[current - 1]);
  };

  const handleNextClick = () => {
    const current = CONTROL_STEPS.indexOf(activeTab);

    setActiveTab(CONTROL_STEPS[current + 1]);
  };

  const handleSubmitClick = () => {
    if (mode === DETAIL_MODE.NEW)
      showConfirmModal({
        title: 'Confirm Submission',
        description:
          'Are you sure you want to submit this form? This action cannot be undone.',
        onConfirm: actions.createControl,
      });
    else
      showConfirmModal({
        title: 'Confirm Submission',
        description:
          'Are you sure you want to submit this draft? This action cannot be undone.',
        onConfirm: actions.submitDraft,
      });
  };

  const [rejectReason, setRejectReason] = useState('');
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);

  const handleRejectClick = () => {
    setRejectReason('');
    setIsRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    await actions.reject(rejectReason);
    setIsRejectDialogOpen(false);
    setRejectReason('');
  };

  const handleReturnClick = () =>
    showConfirmModal({
      title: 'Return to Requestor',
      description:
        'Are you sure you want to return this change control to the requestor with the current comments?',
      onConfirm: actions.returnToRequestor,
    });

  const handleApproveClick = () =>
    showConfirmModal({
      title: 'Approve Change Control',
      description:
        'Are you sure you want to approve this change control? This action cannot be undone.',
      onConfirm: actions.approve,
    });

  return (
    <>
      {mode === DETAIL_MODE.STATIC && (
        <div className="flex items-center space-x-2">
          {allowedActions.includes('approve') && blockingCommentsCount > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-50 text-xs text-amber-700 whitespace-nowrap">
              <TriangleAlert className="shrink-0 h-3.5 w-3.5" />
              <span>
                {blockingCommentsCount} unresolved comment
                {blockingCommentsCount > 1 ? 's' : ''}
              </span>
            </div>
          )}
          {allowedActions.includes('reject') && (
            <Button
              className="align-top bg-red-500"
              onClick={handleRejectClick}
            >
              REJECT
            </Button>
          )}
          {allowedActions.includes('returnToRequestor') && (
            <Button
              className="align-top bg-red-500"
              disabled={reviews.length === 0}
              onClick={handleReturnClick}
            >
              RETURN TO REQUESTOR
            </Button>
          )}
          {allowedActions.includes('approve') && (
            <Button
              className="align-top"
              disabled={!canApprove || blockingCommentsCount > 0}
              onClick={handleApproveClick}
            >
              APPROVE
            </Button>
          )}
          {allowedActions.includes('newRevision') && actions.newRevision && (
            <Button
              className="align-top"
              onClick={actions.newRevision}
              variant="outline"
            >
              NEW REVISION
            </Button>
          )}
        </div>
      )}
      {mode === DETAIL_MODE.NEW && !isFirstTab && (
        <Button
          className="align-top"
          onClick={handlePreviousClick}
          variant="outline"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
      )}
      {mode === DETAIL_MODE.NEW && !isLastTab && (
        <Button
          className="align-top"
          onClick={handleNextClick}
          variant="outline"
        >
          Next
          <ArrowRight className="h-4 w-4 mr-2" />
        </Button>
      )}
      {((mode === DETAIL_MODE.NEW && isLastTab) ||
        mode === DETAIL_MODE.EDIT) && (
        <Button
          className="align-top"
          disabled={riskGrading !== 'low' && riskResidual !== '0'}
          onClick={handleSubmitClick}
        >
          Submit
          <Check className="h-4 w-4 mr-2" />
        </Button>
      )}

      {/* ── Reject Dialog ───────────────────── */}
      <Dialog
        open={isRejectDialogOpen}
        onOpenChange={(open) => {
          setIsRejectDialogOpen(open);
          if (!open) setRejectReason('');
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reject Change Control</DialogTitle>
            <DialogDescription>
              You are about to reject this change control. Please provide a
              reason below.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium mb-1 block">
              Reason for Rejection
            </label>
            <Textarea
              className="font-mono resize-none"
              onChange={({ target }) => setRejectReason(target.value)}
              placeholder="Enter reason for rejection..."
              rows={4}
              value={rejectReason}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsRejectDialogOpen(false);
                setRejectReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-red-500"
              disabled={!rejectReason.trim()}
              onClick={handleRejectConfirm}
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ProgressionActionsHead = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const isFromPending = location.state === 'pending';

  const handleBackClick = () =>
    navigate('/controls', {
      state: isFromPending ? 'pending' : 'master',
    });

  const handlePrintClick = () => {
    window.print();
  };

  return (
    <>
      <Button onClick={handleBackClick} variant="outline">
        <Reply className="h-4 w-4 mr-2" />
        {isFromPending ? 'Back to Pending List' : 'Back to Master List'}
      </Button>
      <Button onClick={handlePrintClick} variant="outline">
        <Printer className="w-2 h-2" />
        Print
      </Button>
    </>
  );
};

export const ProgressionFormCompletionAction = () => {
  const { form } = useControlContext();
  const isComplete = ['APPROVED', 'RELEASED'].includes(form?.status);
  const reviewerCount = form?.reviewerPipeline?.length || 0;
  const canComplete =
    form?.status === 'REVIEWING' &&
    reviewerCount > 0 &&
    form?.currentActorIndex === reviewerCount - 1;

  const actions = useControlActions();

  const { showConfirmModal } = useConfirmModal();

  const handleCompletionClick = () =>
    showConfirmModal({
      title: 'Mark Additional Training as Completed',
      description:
        'Are you sure you want to mark additional training as completed? This action cannot be undone.',
      onConfirm: actions.completeProgression,
    });

  return (
    <Button
      className="align-top"
      disabled={!canComplete}
      onClick={handleCompletionClick}
      variant="outline"
    >
      <CheckCheck className="h-4 w-4 mr-2" />
      {isComplete ? 'Completed' : 'Mark Additional Training as Completed'}
    </Button>
  );
};

export const ProgressionRecordsDelete = ({ id }) => {
  const actions = useControlActions();

  const { showConfirmModal } = useConfirmModal();

  const handleDeleteClick = () =>
    showConfirmModal({
      title: 'Delete',
      description: 'Are you sure you want to delete the training record?',
      onConfirm: () => actions.deleteRecord(id),
    });

  return (
    <button className="mr-4" onClick={handleDeleteClick}>
      <CircleX className="h-4 w-4" />
    </button>
  );
};

export const ProgressionRecordsUpload = ({ disabled = false }) => {
  const actions = useControlActions();

  const { showConfirmModal } = useConfirmModal();

  const handleUploadClick = () =>
    showConfirmModal({
      title: 'Update',
      description: 'Are you sure you want to update the training records?',
      onConfirm: actions.updateRecords,
    });

  return (
    <Button
      className="align-top"
      disabled={disabled}
      onClick={handleUploadClick}
      variant="outline"
    >
      <FileUp className="h-4 w-4 mr-2" />
      Upload
    </Button>
  );
};
